import {
	alpha,
	Box,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import {
	CircleUserRound,
	FileText,
	Sparkles,
	Target,
	Upload,
} from "lucide-react";
import { type FC, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardHeaderActions } from "web_myplann/Dashboard/Components/DashboardHeaderActions";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { EmptyStateButton } from "web_myplann/Shared/Components/EmptyStateButton";
import { useContactAdvisor } from "web_myplann/Shared/Hooks/useContactAdvisor";
import { device } from "web_myplann/Utils/device";

const EmptyStateResources = Resources.Dashboard.EmptyState;

export const DashboardEmptyState: FC = () => {
	const theme = useTheme();
	const { palette } = theme;
	const { t } = useResource();
	const navigate = useNavigate();
	const { openChat } = useContactAdvisor();

	useEffect(() => {
		device.setStatusBarOverlay(true).catch(() => {});
		return () => {
			device.setStatusBarOverlay(false).catch(() => {});
		};
	}, []);

	return (
		<Box
			position="relative"
			px={1.6}
			pt="calc(var(--mobile-safe-area-top, 0px) + 0.5rem)"
			pb={2.4}
			sx={{ background: theme.gradients.premium }}
		>
			<Box
				position="absolute"
				overflow="hidden"
				sx={{ inset: 0, pointerEvents: "none" }}
			>
				<Sparkles
					size={64}
					style={{
						position: "absolute",
						top: 25,
						right: 24,
						color: alpha(palette.darkmodeText.white, 0.05),
					}}
				/>
				<Target
					size={48}
					style={{
						position: "absolute",
						bottom: 16,
						left: 16,
						color: alpha(palette.darkmodeText.white, 0.05),
					}}
				/>
			</Box>

			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				position="relative"
			>
				<IconButton
					onClick={() => navigate(Routing.Profile.ProfileDetail.path())}
				>
					<CircleUserRound
						size={theme.iconSize.lg}
						color={palette.darkmodeText.white}
					/>
				</IconButton>
				<Stack direction="row">
					<DashboardHeaderActions variant="gradient" />
				</Stack>
			</Stack>

			<Stack
				alignItems="center"
				textAlign="center"
				mt={2}
				mb={5}
				position="relative"
			>
				<Typography
					variant="bodyMedium"
					color={alpha(palette.darkmodeText.white, 0.8)}
					mb={0.5}
				>
					{t(EmptyStateResources.eyebrow)}
				</Typography>
				<Typography variant="h2" color={palette.darkmodeText.white} mb={1}>
					{t(EmptyStateResources.title)}
				</Typography>
				<Typography
					variant="caption"
					color={alpha(palette.darkmodeText.white, 0.7)}
					lineHeight={1.5}
					maxWidth={260}
				>
					{t(EmptyStateResources.description)}
				</Typography>
			</Stack>

			<Stack gap={1} position="relative">
				<EmptyStateButton
					icon={
						<FileText
							size={theme.iconSize.md}
							color={palette.darkmodeText.white}
						/>
					}
					iconBg={palette.blue[600]}
					label={t(EmptyStateResources.addContractButton)}
					onClick={() => openChat(t(EmptyStateResources.contractMessage))}
				/>
				<EmptyStateButton
					icon={
						<Upload
							size={theme.iconSize.md}
							color={palette.darkmodeText.white}
						/>
					}
					iconBg={palette.blue[500]}
					label={t(EmptyStateResources.uploadDocumentButton)}
					onClick={() => openChat(t(EmptyStateResources.documentMessage))}
				/>
				<EmptyStateButton
					icon={
						<Target
							size={theme.iconSize.md}
							color={palette.darkmodeText.white}
						/>
					}
					iconBg={palette.green[500]}
					label={t(EmptyStateResources.addGoalButton)}
					onClick={() => navigate(getRoute(Routing.Goals.GoalFormCreate))}
				/>
			</Stack>
		</Box>
	);
};
